import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './SpinWheel.css';

// Contract ABIs
const SPINWHEEL_ABI = [
  "function spin(uint8 colorChoice, uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 playerChoice, uint8 result, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256 redWinChance, uint256 blueWinChance, uint256 greenWinChance, uint256 yellowWinChance, uint256 colorWinPayout))",
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function calculateHouseEdge() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function admin() external view returns (address)", 
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function updateWinChances(uint256 noWin, uint256 red, uint256 blue, uint256 green, uint256 yellow) external",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 playerChoice, uint8 result, uint256 grossPayout, uint256 netPayout, bool won)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  SPINWHEEL: "0x61e3cF1D6cC651332522D676Dd6E95f3D40f6774", // Add your SpinWheel contract address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"   // Add your BLB token contract address
};

// Color configurations with battle-themed emojis
const WHEEL_COLORS = [
  { name: 'RED', color: '#ef4444', emoji: '⚔️', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', battleTheme: 'SWORD' },
  { name: 'BLUE', color: '#3b82f6', emoji: '🛡️', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', battleTheme: 'SHIELD' },
  { name: 'GREEN', color: '#10b981', emoji: '🏹', gradient: 'linear-gradient(135deg, #10b981, #059669)', battleTheme: 'ARROW' },
  { name: 'YELLOW', color: '#f59e0b', emoji: '👑', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', battleTheme: 'CROWN' }
];

// 🎵 WHEEL SOUND SYSTEM 🎵
const playWheelSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'wheelSpin':
        // Spinning wheel sound - ascending tones
        createComplexSound([
          { freq: 200, duration: 0.1, wave: 'sawtooth', delay: 0 },
          { freq: 300, duration: 0.1, wave: 'triangle', delay: 100 },
          { freq: 400, duration: 0.15, wave: 'sine', delay: 200 },
          { freq: 500, duration: 0.2, wave: 'sine', delay: 300 },
          { freq: 600, duration: 0.25, wave: 'triangle', delay: 400 },
          { freq: 700, duration: 0.3, wave: 'sine', delay: 500 }
        ], 0.15);
        break;
        
      case 'wheelStop':
        // Wheel stopping sound
        createComplexSound([
          { freq: 400, duration: 0.2, wave: 'triangle', delay: 0 },
          { freq: 300, duration: 0.3, wave: 'sawtooth', delay: 100 },
          { freq: 200, duration: 0.4, wave: 'sine', delay: 200 }
        ], 0.25);
        break;
        
      case 'victory':
        createComplexSound([
          { freq: 523, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.3, wave: 'triangle', delay: 100 },
          { freq: 784, duration: 0.3, wave: 'triangle', delay: 200 },
          { freq: 1047, duration: 0.5, wave: 'sine', delay: 300 }
        ], 0.3);
        break;
        
      case 'defeat':
        createComplexSound([
          { freq: 800, duration: 0.2, wave: 'triangle', delay: 0 },
          { freq: 600, duration: 0.2, wave: 'triangle', delay: 100 },
          { freq: 400, duration: 0.3, wave: 'sawtooth', delay: 200 },
          { freq: 200, duration: 0.4, wave: 'sawtooth', delay: 300 }
        ], 0.2);
        break;
        
      case 'buttonHover':
        createTone(800, 0.1, 'sine');
        break;
        
      case 'buttonClick':
        createComplexSound([
          { freq: 1000, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 1200, duration: 0.05, wave: 'sine', delay: 20 }
        ], 0.05);
        break;
        
      case 'colorSelect':
        createComplexSound([
          { freq: 400, duration: 0.1, wave: 'sawtooth', delay: 0 },
          { freq: 800, duration: 0.15, wave: 'triangle', delay: 50 },
          { freq: 1200, duration: 0.2, wave: 'sine', delay: 100 }
        ], 0.15);
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const SpinWheel = ({ walletData, onBack }) => {
  const [gameState, setGameState] = useState({
    betAmount: '1',
    selectedColor: null,
    isSpinning: false,
    isApproving: false,
    lastResult: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    experience: 0,
    streak: 0,
    showSpinPopup: false // NEW: For popup spin
  });

  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalPayouts: '0',
    houseEdge: '5',
    effectiveMultiplier: '3.42',
    playersOnline: 0
  });

  const [gameHistory, setGameHistory] = useState([]);
  const [animationState, setAnimationState] = useState('idle');
  const [spinResult, setSpinResult] = useState(null);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [popupWheelRotation, setPopupWheelRotation] = useState(0); // NEW: Separate rotation for popup
  const [particles, setParticles] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    spinWheel: null,
    blbToken: null
  });
  const wheelRef = useRef(null);
  
  // Admin Panel State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminState, setAdminState] = useState({
    houseBalance: '0',
    isPaused: false,
    fundAmount: '',
    withdrawAmount: '',
    minBet: '1',
    maxBet: '1000000',
    newWinChances: {
      noWin: '6.0',    // 6% instead of 600 basis points
      red: '23.5',     // 23.5% instead of 2350 basis points
      blue: '23.5',    // 23.5% instead of 2350 basis points
      green: '23.5',   // 23.5% instead of 2350 basis points
      yellow: '23.5'   // 23.5% instead of 2350 basis points
    }
  });

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playWheelSound(type, volume);
    }
  };

  // 🔗 Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ spinWheel: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const spinWheelContract = new ethers.Contract(
        CONTRACT_ADDRESSES.SPINWHEEL,
        SPINWHEEL_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        spinWheel: spinWheelContract,
        blbToken: blbTokenContract
      });

      // Load data after contracts are set
      setTimeout(() => {
        loadGameData(spinWheelContract);
        loadBalance(blbTokenContract, spinWheelContract);
        checkAdminAccess(spinWheelContract);
      }, 500);

    } catch (error) {
      console.log('Error initializing contracts:', error);
      setContracts({ spinWheel: null, blbToken: null });
    }
  };

  // 🔒 Check admin access
  const checkAdminAccess = async (contract = contracts.spinWheel) => {
    if (!contract || !walletData?.account) return;

    try {
      // Check if user is owner
      const owner = await contract.owner();
      const userIsOwner = owner.toLowerCase() === walletData.account.toLowerCase();
      
      // Check if user is admin
      const admin = await contract.admin();
      const userIsAdmin = admin.toLowerCase() === walletData.account.toLowerCase();

      setIsOwner(userIsOwner);
      setIsAdmin(userIsAdmin);

      // Load admin data if user has admin access
      if (userIsOwner || userIsAdmin) {
        await loadAdminData(contract);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsOwner(false);
      setIsAdmin(false);
    }
  };

  // 📊 Load admin data
  const loadAdminData = async (contract = contracts.spinWheel) => {
    if (!contract) return;

    try {
      const [houseBalance, isPaused, betLimits] = await Promise.all([
        contract.houseBalance(),
        contract.paused(),
        Promise.all([contract.minBet(), contract.maxBet()])
      ]);

      setAdminState(prev => ({
        ...prev,
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(betLimits[0]),
        maxBet: ethers.utils.formatEther(betLimits[1])
      }));
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // 🎮 Admin Functions
  const handlePauseGame = async () => {
    if (!contracts.spinWheel) return;

    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);

      const tx = adminState.isPaused 
        ? await contracts.spinWheel.unpauseGame()
        : await contracts.spinWheel.pauseGame();

      setSuccessMessage(`Game ${adminState.isPaused ? 'unpaused' : 'paused'} transaction submitted...`);
      await tx.wait();
      
      await loadAdminData();
      setSuccessMessage(`✅ Game ${adminState.isPaused ? 'unpaused' : 'paused'} successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Pause/Unpause error:', error);
      setErrorMessage(`Failed to ${adminState.isPaused ? 'unpause' : 'pause'} game: ${error.reason || error.message}`);
    }
  };

  const handleFundHouse = async () => {
    if (!contracts.spinWheel || !adminState.fundAmount) return;

    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);

      const amount = ethers.utils.parseEther(adminState.fundAmount);
      const tx = await contracts.spinWheel.fundHouse(amount);
      
      setSuccessMessage('Fund house transaction submitted...');
      await tx.wait();
      
      await Promise.all([loadAdminData(), loadBalance()]);
      setAdminState(prev => ({ ...prev, fundAmount: '' }));
      setSuccessMessage('✅ House funded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Fund house error:', error);
      setErrorMessage(`Failed to fund house: ${error.reason || error.message}`);
    }
  };

  const handleEmergencyWithdraw = async () => {
    if (!contracts.spinWheel || !adminState.withdrawAmount) return;

    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);

      const amount = ethers.utils.parseEther(adminState.withdrawAmount);
      const tx = await contracts.spinWheel.withdrawHouse(amount);
      
      setSuccessMessage('Emergency withdraw transaction submitted...');
      await tx.wait();
      
      await Promise.all([loadAdminData(), loadBalance()]);
      setAdminState(prev => ({ ...prev, withdrawAmount: '' }));
      setSuccessMessage('✅ Emergency withdrawal successful!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Emergency withdraw error:', error);
      setErrorMessage(`Failed to withdraw: ${error.reason || error.message}`);
    }
  };

  const handleSetBetLimits = async () => {
    if (!contracts.spinWheel) return;

    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);

      const minBetWei = ethers.utils.parseEther(adminState.minBet);
      const maxBetWei = ethers.utils.parseEther(adminState.maxBet);
      
      const tx = await contracts.spinWheel.setBetLimits(minBetWei, maxBetWei);
      
      setSuccessMessage('Set bet limits transaction submitted...');
      await tx.wait();
      
      await loadAdminData();
      setSuccessMessage('✅ Bet limits updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Set bet limits error:', error);
      setErrorMessage(`Failed to set bet limits: ${error.reason || error.message}`);
    }
  };

  const handleUpdateWinChances = async () => {
    if (!contracts.spinWheel) return;

    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);

      const { noWin, red, blue, green, yellow } = adminState.newWinChances;
      
      // Convert percentages to basis points for the smart contract
      const noWinBP = Math.round(parseFloat(noWin) * 100);
      const redBP = Math.round(parseFloat(red) * 100);
      const blueBP = Math.round(parseFloat(blue) * 100);
      const greenBP = Math.round(parseFloat(green) * 100);
      const yellowBP = Math.round(parseFloat(yellow) * 100);
      
      const totalPercentage = parseFloat(noWin) + parseFloat(red) + parseFloat(blue) + parseFloat(green) + parseFloat(yellow);
      
      if (Math.abs(totalPercentage - 100) > 0.1) { // Allow small rounding differences
        setErrorMessage('Win chances must total exactly 100%');
        return;
      }

      const tx = await contracts.spinWheel.updateWinChances(noWinBP, redBP, blueBP, greenBP, yellowBP);
      
      setSuccessMessage('Update win chances transaction submitted...');
      await tx.wait();
      
      await loadGameData();
      setSuccessMessage('✅ Win chances updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Update win chances error:', error);
      setErrorMessage(`Failed to update win chances: ${error.reason || error.message}`);
    }
  };

  // 🎮 Particle effects
  useEffect(() => {
    if (animationState === 'result' && spinResult?.won) {
      const newParticles = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 6 + 3,
        life: 100,
        color: WHEEL_COLORS[spinResult.result]?.color || '#ffd700',
        velocity: {
          x: (Math.random() - 0.5) * 4,
          y: Math.random() * -3 - 1
        }
      }));
      setParticles(newParticles);
      
      const interval = setInterval(() => {
        setParticles(prev => prev.map(p => ({ 
          ...p, 
          life: p.life - 2,
          x: p.x + p.velocity.x,
          y: p.y + p.velocity.y
        })).filter(p => p.life > 0));
      }, 50);
      
      setTimeout(() => clearInterval(interval), 5000);
    }
  }, [animationState, spinResult]);

  // 🔗 Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  // 📊 Load game data from contract
  const loadGameData = async (contract = contracts.spinWheel) => {
    if (!contract) return;

    try {
      const [stats, recentGames, houseEdge, effectiveMultiplier] = await Promise.all([
        contract.getGameStats(),
        contract.getRecentGames(5).catch(() => []),
        contract.calculateHouseEdge(),
        contract.getEffectiveMultiplier()
      ]);

      setGameStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalPayouts: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: (parseInt(houseEdge.toString()) / 100).toString(),
        effectiveMultiplier: (parseInt(effectiveMultiplier.toString()) / 100).toString(),
        playersOnline: Math.floor(Math.random() * 500) + 100 // Keep this for UI purposes
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          playerChoice: parseInt(game.playerChoice),
          result: parseInt(game.result),
          won: game.won,
          betAmount: ethers.utils.formatEther(game.betAmount),
          netPayout: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error loading game data:', error);
    }
  };

  // 💰 Load BLB balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.spinWheel) => {
    if (!tokenContract || !gameContract || !walletData?.account) return;

    try {
      const [balance, allowance] = await Promise.all([
        tokenContract.balanceOf(walletData.account),
        tokenContract.allowance(walletData.account, gameContract.address)
      ]);

      setGameState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  // 🎯 Handle wheel spin
  const handleSpin = async () => {
    if (gameState.selectedColor === null) {
      setErrorMessage('Please select a color first');
      return;
    }

    if (!contracts.spinWheel || !contracts.blbToken) {
      setErrorMessage('Contracts not loaded. Please refresh and try again.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      // Validate bet amount
      const betAmount = parseFloat(gameState.betAmount);
      const balance = parseFloat(gameState.balance);

      if (betAmount <= 0) {
        setErrorMessage('Bet amount must be greater than 0');
        return;
      }

      if (betAmount > balance) {
        setErrorMessage('Insufficient balance');
        return;
      }

      playGameSound('wheelSpin', 0.4);
      
      // Show popup and start spinning
      setGameState(prev => ({ 
        ...prev, 
        isSpinning: true, 
        showSpinPopup: true 
      }));
      setAnimationState('spinning');

      // Start popup wheel rotation animation
      const baseRotation = popupWheelRotation;
      const spinRotations = 5 + Math.random() * 3; // 5-8 full rotations
      const finalRotation = baseRotation + (spinRotations * 360);
      setPopupWheelRotation(finalRotation);

      // Execute blockchain transaction
      const betAmountWei = ethers.utils.parseEther(gameState.betAmount);
      const tx = await contracts.spinWheel.spin(gameState.selectedColor, betAmountWei);
      
      setSuccessMessage('Transaction submitted! Spinning wheel...');
      
      const receipt = await tx.wait();
      
      setTimeout(() => {
        playGameSound('wheelStop', 0.4);
      }, 3000);
      
      const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
      
      if (gamePlayedEvent) {
        const [player, betAmount, playerChoice, result, grossPayout, netPayout, won] = gamePlayedEvent.args;
        
        const gameResult = {
          player,
          betAmount: ethers.utils.formatEther(betAmount),
          playerChoice: parseInt(playerChoice),
          result: parseInt(result),
          grossPayout: ethers.utils.formatEther(grossPayout),
          netPayout: ethers.utils.formatEther(netPayout),
          won,
          timestamp: Date.now()
        };

        setTimeout(() => {
          handleGameResult(gameResult);
        }, 3000);
        
        // Reload data after transaction
        await Promise.all([loadGameData(), loadBalance()]);
      } else {
        throw new Error('Game event not found in transaction receipt');
      }

    } catch (error) {
      console.error('Spin error:', error);
      
      // Handle specific error types
      if (error.code === 4001) {
        setErrorMessage('Transaction rejected by user');
      } else if (error.code === -32603) {
        setErrorMessage('Transaction failed. Please check your balance and allowance.');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient gas or token balance');
      } else if (error.message.includes('allowance')) {
        setErrorMessage('Please approve token spending first');
      } else {
        setErrorMessage(`Spin failed: ${error.reason || error.message}`);
      }
      
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isSpinning: false, 
        showSpinPopup: false 
      }));
      setSuccessMessage('');
    }
  };

  const handleGameResult = (gameResult) => {
    setSpinResult(gameResult);
    setAnimationState('result');

    setTimeout(() => {
      if (gameResult.won) {
        playGameSound('victory', 0.6);
        setSuccessMessage(`🎉 You won ${formatNumber(gameResult.netPayout)} BLB!`);
      } else {
        playGameSound('defeat', 0.4);
        setErrorMessage(`💸 You lost ${formatNumber(gameResult.betAmount)} BLB`);
      }
    }, 500);

    // Update player stats (for UI only)
    setGameState(prev => ({
      ...prev,
      isSpinning: false,
      streak: gameResult.won ? prev.streak + 1 : 0,
      experience: prev.experience + (gameResult.won ? 75 : 15),
      playerLevel: Math.floor((prev.experience + (gameResult.won ? 75 : 15)) / 150) + 1
    }));

    // Close popup after showing result
    setTimeout(() => {
      setAnimationState('idle');
      setSpinResult(null);
      setGameState(prev => ({ 
        ...prev, 
        selectedColor: null,
        isSpinning: false,
        showSpinPopup: false // Close popup
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // 🔓 Handle token approval
  const handleApprove = async () => {
    if (!contracts.blbToken || !contracts.spinWheel) {
      setErrorMessage('Contracts not loaded. Please refresh and try again.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound('buttonClick', 0.3);
      setGameState(prev => ({ ...prev, isApproving: true }));

      const maxAmount = ethers.constants.MaxUint256;
      const tx = await contracts.blbToken.approve(contracts.spinWheel.address, maxAmount);
      
      setSuccessMessage('Approval transaction submitted...');
      await tx.wait();
      
      await loadBalance();
      setSuccessMessage('✅ Token approval successful!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Approval rejected by user');
      } else {
        setErrorMessage(`Approval failed: ${error.reason || error.message}`);
      }
    } finally {
      setGameState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const handleColorSelect = (colorIndex) => {
    playGameSound('colorSelect', 0.3);
    setGameState(prev => ({ ...prev, selectedColor: colorIndex }));
  };

  const needsApproval = () => {
    return parseFloat(gameState.allowance) < parseFloat(gameState.betAmount);
  };

  const canSpin = () => {
    return connected && 
           contracts.spinWheel &&
           contracts.blbToken &&
           gameState.selectedColor !== null && 
           parseFloat(gameState.balance) >= parseFloat(gameState.betAmount) &&
           parseFloat(gameState.betAmount) > 0 &&
           !needsApproval() &&
           !gameState.isSpinning;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getResultText = (result) => {
    if (result === 4) return 'HOUSE WINS';
    return WHEEL_COLORS[result]?.name || 'UNKNOWN';
  };

  if (!connected) {
    return (
      <div className="spinwheel-battle-arena">
        <div className="spinwheel-connect-screen">
          <div className="spinwheel-connect-icon">🎡</div>
          <h3 className="spinwheel-connect-title">🎰 CONNECT WALLET</h3>
          <p className="spinwheel-connect-desc">Connect your wallet to access the SpinWin Wheel Arena and start spinning for real rewards!</p>
        </div>
      </div>
    );
  }

  if (!contracts.spinWheel || !contracts.blbToken) {
    return (
      <div className="spinwheel-battle-arena">
        <div className="spinwheel-connect-screen">
          <div className="spinwheel-connect-icon">⏳</div>
          <h3 className="spinwheel-connect-title">🔄 LOADING</h3>
          <p className="spinwheel-connect-desc">Loading smart contracts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="spinwheel-battle-arena">
      {/* Header */}
      <div className="spinwheel-header-bar">
        <button 
          className="spinwheel-back-btn" 
          onClick={() => {
            playGameSound('buttonClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('buttonHover', 0.2)}
        >
          ← Retreat
        </button>
        <div className="spinwheel-title-section">
          <h2 className="spinwheel-main-title">🎡 SPINWIN WHEEL</h2>
          <div className="spinwheel-subtitle">{gameStats.effectiveMultiplier}x Multiplier</div>
        </div>
        <div className="spinwheel-balance-display">
          💎 {formatNumber(gameState.balance)} BLB
          <div className="spinwheel-player-level">
            LVL {gameState.playerLevel}
          </div>
          <button
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('buttonClick', 0.2);
            }}
            className="spinwheel-sound-btn"
            title={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          {(isOwner || isAdmin) && (
            <button
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('buttonClick', 0.3);
              }}
              onMouseEnter={() => playGameSound('buttonHover', 0.2)}
              className={`spinwheel-admin-toggle-btn ${showAdminPanel ? 'active' : ''}`}
              title="Admin Panel"
            >
              ⚙️
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="spinwheel-message spinwheel-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="spinwheel-message spinwheel-success-msg">
          {successMessage}
        </div>
      )}

      <div className="spinwheel-game-layout">
        {/* Admin Panel */}
        {showAdminPanel && (isOwner || isAdmin) && (
          <div className="spinwheel-admin-panel">
            <div className="spinwheel-admin-header">
              <h3>🛡️ ADMIN CONTROL PANEL</h3>
              <div className="admin-role-badge">
                {isOwner ? '👑 OWNER' : '⚡ ADMIN'}
              </div>
            </div>

            <div className="spinwheel-admin-grid">
              {/* House Balance & Controls */}
              <div className="spinwheel-admin-section">
                <h4>🏦 HOUSE MANAGEMENT</h4>
                <div className="admin-stat-display">
                  <span className="admin-stat-label">House Balance:</span>
                  <span className="admin-stat-value">{formatNumber(adminState.houseBalance)} BLB</span>
                </div>

                <div className="admin-input-group">
                  <input
                    type="number"
                    placeholder="Amount to fund"
                    value={adminState.fundAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, fundAmount: e.target.value }))}
                    className="spinwheel-admin-input"
                    min="0"
                    step="1"
                  />
                  <button
                    onClick={handleFundHouse}
                    className="spinwheel-admin-btn fund-btn"
                    disabled={!adminState.fundAmount || parseFloat(adminState.fundAmount) <= 0}
                  >
                    💰 FUND HOUSE
                  </button>
                </div>

                {isOwner && (
                  <div className="admin-input-group">
                    <input
                      type="number"
                      placeholder="Amount to withdraw"
                      value={adminState.withdrawAmount}
                      onChange={(e) => setAdminState(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                      className="spinwheel-admin-input"
                      min="0"
                      step="1"
                    />
                    <button
                      onClick={handleEmergencyWithdraw}
                      className="spinwheel-admin-btn emergency-btn"
                      disabled={!adminState.withdrawAmount || parseFloat(adminState.withdrawAmount) <= 0}
                    >
                      🚨 EMERGENCY WITHDRAW
                    </button>
                  </div>
                )}
              </div>

              {/* Game Controls */}
              <div className="spinwheel-admin-section">
                <h4>🎮 GAME CONTROLS</h4>
                <button
                  onClick={handlePauseGame}
                  className={`spinwheel-admin-btn ${adminState.isPaused ? 'unpause-btn' : 'pause-btn'}`}
                >
                  {adminState.isPaused ? '▶️ UNPAUSE GAME' : '⏸️ PAUSE GAME'}
                </button>
                
                <div className="admin-status-display">
                  <span className="admin-status-label">Game Status:</span>
                  <span className={`admin-status-value ${adminState.isPaused ? 'paused' : 'active'}`}>
                    {adminState.isPaused ? '🔴 PAUSED' : '🟢 ACTIVE'}
                  </span>
                </div>
              </div>

              {/* Bet Limits */}
              <div className="spinwheel-admin-section">
                <h4>💰 BET LIMITS</h4>
                <div className="admin-input-row">
                  <div className="admin-input-group">
                    <label>Min Bet (BLB)</label>
                    <input
                      type="number"
                      value={adminState.minBet}
                      onChange={(e) => setAdminState(prev => ({ ...prev, minBet: e.target.value }))}
                      className="spinwheel-admin-input"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="admin-input-group">
                    <label>Max Bet (BLB)</label>
                    <input
                      type="number"
                      value={adminState.maxBet}
                      onChange={(e) => setAdminState(prev => ({ ...prev, maxBet: e.target.value }))}
                      className="spinwheel-admin-input"
                      min="0"
                      step="1"
                    />
                  </div>
                </div>
                <button
                  onClick={handleSetBetLimits}
                  className="spinwheel-admin-btn update-btn"
                  disabled={parseFloat(adminState.minBet) >= parseFloat(adminState.maxBet)}
                >
                  📊 UPDATE BET LIMITS
                </button>
              </div>

              {/* Win Chances */}
              <div className="spinwheel-admin-section win-chances-section">
                <h4>🎯 WIN CHANCES (PERCENTAGE)</h4>
                <div className="win-chances-grid">
                  <div className="chance-input">
                    <label>🏠 House (No Win)</label>
                    <div className="percentage-input-wrapper">
                      <input
                        type="number"
                        value={adminState.newWinChances.noWin}
                        onChange={(e) => setAdminState(prev => ({
                          ...prev,
                          newWinChances: { ...prev.newWinChances, noWin: e.target.value }
                        }))}
                        className="spinwheel-admin-input percentage-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="percentage-symbol">%</span>
                    </div>
                  </div>
                  <div className="chance-input">
                    <label>🔴 Red</label>
                    <div className="percentage-input-wrapper">
                      <input
                        type="number"
                        value={adminState.newWinChances.red}
                        onChange={(e) => setAdminState(prev => ({
                          ...prev,
                          newWinChances: { ...prev.newWinChances, red: e.target.value }
                        }))}
                        className="spinwheel-admin-input percentage-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="percentage-symbol">%</span>
                    </div>
                  </div>
                  <div className="chance-input">
                    <label>🔵 Blue</label>
                    <div className="percentage-input-wrapper">
                      <input
                        type="number"
                        value={adminState.newWinChances.blue}
                        onChange={(e) => setAdminState(prev => ({
                          ...prev,
                          newWinChances: { ...prev.newWinChances, blue: e.target.value }
                        }))}
                        className="spinwheel-admin-input percentage-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="percentage-symbol">%</span>
                    </div>
                  </div>
                  <div className="chance-input">
                    <label>🟢 Green</label>
                    <div className="percentage-input-wrapper">
                      <input
                        type="number"
                        value={adminState.newWinChances.green}
                        onChange={(e) => setAdminState(prev => ({
                          ...prev,
                          newWinChances: { ...prev.newWinChances, green: e.target.value }
                        }))}
                        className="spinwheel-admin-input percentage-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="percentage-symbol">%</span>
                    </div>
                  </div>
                  <div className="chance-input">
                    <label>🟡 Yellow</label>
                    <div className="percentage-input-wrapper">
                      <input
                        type="number"
                        value={adminState.newWinChances.yellow}
                        onChange={(e) => setAdminState(prev => ({
                          ...prev,
                          newWinChances: { ...prev.newWinChances, yellow: e.target.value }
                        }))}
                        className="spinwheel-admin-input percentage-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="percentage-symbol">%</span>
                    </div>
                  </div>
                </div>
                <div className="total-display">
                  <span>Total: {
                    (parseFloat(adminState.newWinChances.noWin || 0) + 
                     parseFloat(adminState.newWinChances.red || 0) + 
                     parseFloat(adminState.newWinChances.blue || 0) + 
                     parseFloat(adminState.newWinChances.green || 0) + 
                     parseFloat(adminState.newWinChances.yellow || 0)).toFixed(1)
                  }%</span>
                  {Math.abs((parseFloat(adminState.newWinChances.noWin || 0) + 
                             parseFloat(adminState.newWinChances.red || 0) + 
                             parseFloat(adminState.newWinChances.blue || 0) + 
                             parseFloat(adminState.newWinChances.green || 0) + 
                             parseFloat(adminState.newWinChances.yellow || 0)) - 100) < 0.1
                    ? <span className="valid">✅</span> 
                    : <span className="invalid">❌</span>
                  }
                </div>
                <button
                  onClick={handleUpdateWinChances}
                  className="spinwheel-admin-btn update-btn"
                  disabled={Math.abs((parseFloat(adminState.newWinChances.noWin || 0) + 
                                     parseFloat(adminState.newWinChances.red || 0) + 
                                     parseFloat(adminState.newWinChances.blue || 0) + 
                                     parseFloat(adminState.newWinChances.green || 0) + 
                                     parseFloat(adminState.newWinChances.yellow || 0)) - 100) > 0.1}
                >
                  🎯 UPDATE WIN CHANCES
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Game Zone */}
        <div className="spinwheel-game-zone">
          <div className="spinwheel-arena">
            <div 
              ref={wheelRef}
              className="spinwheel-wheel"
              style={{
                transform: `rotate(${wheelRotation}deg)`,
                transition: 'none'
              }}
            >
              {/* Wheel sections - Fixed alignment */}
              <div className="wheel-section red-section">
                <span>⚔️</span>
              </div>
              <div className="wheel-section blue-section">
                <span>🛡️</span>
              </div>
              <div className="wheel-section green-section">
                <span>🏹</span>
              </div>
              <div className="wheel-section yellow-section">
                <span>👑</span>
              </div>
              
              {/* Center hub */}
              <div className="wheel-center">
                <div className="center-logo">🎯</div>
              </div>
            </div>

            {/* Wheel pointer */}
            <div className="wheel-pointer"></div>

            {/* Victory Particles */}
            {particles.map(particle => (
              <div
                key={particle.id}
                style={{
                  position: 'absolute',
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  background: `radial-gradient(circle, ${particle.color}, ${particle.color}80)`,
                  borderRadius: '50%',
                  opacity: particle.life / 100,
                  animation: 'particleFloat 5s ease-out',
                  pointerEvents: 'none',
                  zIndex: 5,
                  boxShadow: `0 0 10px ${particle.color}`
                }}
              />
            ))}
          </div>

          {/* EPIC SPIN POPUP */}
          {gameState.showSpinPopup && (
            <div className="spinwheel-spin-popup-overlay">
              <div className="spinwheel-spin-popup">
                <div className="popup-header">
                  <h2>🎰 SPINNING WHEEL OF WAR!</h2>
                  <div className="bet-info">
                    <span>Stake: {formatNumber(gameState.betAmount)} BLB</span>
                    <span>Choice: {WHEEL_COLORS[gameState.selectedColor]?.emoji} {WHEEL_COLORS[gameState.selectedColor]?.battleTheme}</span>
                  </div>
                </div>

                <div className="popup-wheel-container">
                  <div 
                    className={`popup-wheel ${
                      animationState === 'spinning' ? 'spinning-animation' :
                      animationState === 'result' && spinResult?.won ? 'victory-animation' :
                      animationState === 'result' && !spinResult?.won ? 'defeat-animation' : ''
                    }`}
                    style={{
                      transform: `rotate(${popupWheelRotation}deg)`,
                      transition: animationState === 'spinning' ? 'transform 3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
                    }}
                  >
                    {/* Popup Wheel sections - Properly aligned */}
                    <div className="popup-wheel-section popup-red-section">
                      <span>⚔️</span>
                    </div>
                    <div className="popup-wheel-section popup-blue-section">
                      <span>🛡️</span>
                    </div>
                    <div className="popup-wheel-section popup-green-section">
                      <span>🏹</span>
                    </div>
                    <div className="popup-wheel-section popup-yellow-section">
                      <span>👑</span>
                    </div>
                    
                    {/* Center hub */}
                    <div className="popup-wheel-center">
                      <div className="popup-center-logo">🎯</div>
                    </div>
                  </div>

                  {/* Popup Wheel pointer */}
                  <div className="popup-wheel-pointer"></div>
                </div>

                {/* Result Display in Popup */}
                {animationState === 'result' && spinResult && (
                  <div className="popup-result-display">
                    <div className={`popup-result-content ${spinResult.won ? 'popup-victory' : 'popup-defeat'}`}>
                      <div className="popup-result-icon">
                        {spinResult.won ? '🎊' : '💥'}
                      </div>
                      <h3 className="popup-result-title">
                        {spinResult.won ? '🏆 EPIC VICTORY!' : '⚔️ VALIANT EFFORT'}
                      </h3>
                      <div className="popup-result-amount">
                        {spinResult.won ? `+${formatNumber(spinResult.netPayout)}` : `-${formatNumber(spinResult.betAmount)}`} BLB
                      </div>
                      <div className="popup-result-desc">
                        {getResultText(spinResult.result)} 
                        {spinResult.result < 4 && ` ${WHEEL_COLORS[spinResult.result]?.emoji}`}
                      </div>
                      {spinResult.won && gameState.streak > 1 && (
                        <div className="popup-streak-display">
                          🔥 {gameState.streak}X BATTLE STREAK! 🔥
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Spinning indicator */}
                {animationState === 'spinning' && (
                  <div className="spinning-indicator">
                    <div className="spinner-text">⚡ WHEEL SPINNING...</div>
                    <div className="spinner-subtext">Determining your fate, warrior!</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="spinwheel-controls">
            {/* Stake Input */}
            <div className="spinwheel-control-panel">
              <label className="spinwheel-control-label">💰 STAKE</label>
              <div>
                <input
                  className="spinwheel-stake-input"
                  type="number"
                  value={gameState.betAmount}
                  onChange={(e) => setGameState(prev => ({ ...prev, betAmount: e.target.value }))}
                  disabled={gameState.isSpinning}
                  min="1"
                  max={gameState.balance}
                  step="1"
                  onFocus={() => playGameSound('buttonClick', 0.1)}
                />
                <div className="spinwheel-preset-buttons">
                  {['1', '10', '100'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('buttonClick', 0.2);
                        setGameState(prev => ({ ...prev, betAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                      disabled={gameState.isSpinning}
                      className="spinwheel-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('buttonClick', 0.2);
                      setGameState(prev => ({ ...prev, betAmount: Math.floor(parseFloat(prev.balance)).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                    disabled={gameState.isSpinning}
                    className="spinwheel-preset-btn max-btn"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            {/* Color Selection */}
            <div className="spinwheel-control-panel">
              <label className="spinwheel-control-label">🎨 CHOOSE COLOR</label>
              <div className="spinwheel-color-grid">
                {WHEEL_COLORS.map((color, index) => (
                  <button
                    key={index}
                    className={`spinwheel-color-btn ${gameState.selectedColor === index ? 'selected' : ''}`}
                    style={{
                      background: gameState.selectedColor === index ? color.gradient : color.gradient,
                      opacity: gameState.selectedColor === index ? 1 : 0.7,
                      transform: gameState.selectedColor === index ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: gameState.selectedColor === index ? `0 0 25px ${color.color}40` : 'none'
                    }}
                    onClick={() => handleColorSelect(index)}
                    onMouseEnter={() => playGameSound('buttonHover', 0.15)}
                    disabled={gameState.isSpinning}
                  >
                    <div className="spinwheel-color-icon">{color.emoji}</div>
                    <span className="spinwheel-color-text">{color.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Action Button */}
            <div className="spinwheel-control-panel">
              {needsApproval() ? (
                <button
                  className="spinwheel-action-btn spinwheel-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={gameState.isApproving}
                >
                  {gameState.isApproving ? '⏳ APPROVING...' : '🔓 APPROVE BLB'}
                </button>
              ) : (
                <button
                  className="spinwheel-action-btn spinwheel-spin-btn"
                  onClick={handleSpin}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={!canSpin()}
                >
                  {gameState.isSpinning ? '🎡 SPINNING...' : '🎰 SPIN WHEEL'}
                </button>
              )}
            </div>

            {/* Potential Reward */}
            {gameState.selectedColor !== null && !gameState.isSpinning && (
              <div className="spinwheel-reward-display">
                🎯 WIN: {formatNumber((parseFloat(gameState.betAmount) * parseFloat(gameStats.effectiveMultiplier)).toString())} BLB
              </div>
            )}
          </div>
        </div>

        {/* Stats Sidebar */}
        <div className="spinwheel-stats-sidebar">
          <div className="spinwheel-stats-panel">
            <h3 className="spinwheel-stats-title">🏆 STATUS</h3>
            <div className="spinwheel-stats-item">
              <span className="spinwheel-stats-label">Level</span>
              <span className="spinwheel-stats-value">{gameState.playerLevel}</span>
            </div>
            <div className="spinwheel-stats-item">
              <span className="spinwheel-stats-label">XP</span>
              <span className="spinwheel-stats-value">{formatNumber(gameState.experience)}</span>
            </div>
            <div className="spinwheel-stats-item">
              <span className="spinwheel-stats-label">Streak</span>
              <span className="spinwheel-stats-value">{gameState.streak}x 🔥</span>
            </div>
          </div>

          

          <div className="spinwheel-stats-panel">
            <h3 className="spinwheel-stats-title">🎯 HOW TO PLAY</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                'Set your stake amount',
                'Choose your color',
                'Click SPIN WHEEL',
                `Win ${gameStats.effectiveMultiplier}x your bet!`
              ].map((step, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    background: 'linear-gradient(45deg, #ef4444, #f59e0b)', 
                    color: '#000', 
                    width: '2rem', 
                    height: '2rem', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: '800', 
                    fontSize: '0.9rem', 
                    flexShrink: 0,
                    boxShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
                  }}>{index + 1}</div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                    {step}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {gameHistory.length > 0 && (
            <div className="spinwheel-stats-panel">
              <h3 className="spinwheel-stats-title">📜 RECENT SPINS</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '200px', overflowY: 'auto' }}>
                {gameHistory.map((spin, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.8rem',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${spin.won ? '#10b981' : '#ef4444'}`,
                  }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {spin.result < 4 ? WHEEL_COLORS[spin.result]?.emoji : '🏠'}
                    </div>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', fontWeight: '600' }}>
                        {formatNumber(spin.betAmount)} BLB
                      </div>
                      <div style={{ 
                        color: spin.won ? '#10b981' : '#ef4444', 
                        fontWeight: '800', 
                        fontSize: '0.9rem'
                      }}>
                        {spin.won ? `+${formatNumber(spin.netPayout)}` : 'LOSS'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpinWheel;